import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { SEND_ALL_SERVICE_NAMES } from './sendAllItemFacts';
import { sendAllMessages as s } from './sendAllMessages';
import { isTicked, type SendAllItemView, type SendAllServiceView } from './sendAllModel';
import { SendAllChipBadge } from './SendAllChipBadge';
import { SendAllItemRow } from './SendAllItemRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Send to all sheet; each part uses its own slice of it */
import styles from './SendAll.module.scss';
/* eslint-enable css-modules/no-unused-class */

const LOGO: Record<SendAllServiceView['service'], string> = { spotify: 'S', youtube: 'YT', facebook: 'f', deezer: 'D' };

interface SendAllServiceRowProps {
  view: SendAllServiceView;
  profile: ArtistProfileResponse;
  ticks: Readonly<Record<string, boolean>>;
  /** Only this service starts ticked (`?sheet=spotify`), or null for every service. */
  only: SendAllServiceView['service'] | null;
  locked: boolean;
  isSending: boolean;
  onToggle: (view: SendAllItemView, on: boolean) => void;
  onRetry: (view: SendAllItemView) => void;
  onConnect: (service: SendAllServiceView['service']) => void;
}

/**
 * One service of "Send to all" (design c `.sv`): the head with the account and Connect / Sign in
 * again / Coming soon, then its items. A service that cannot send folds to its head, except when
 * nothing is connected (c1 shows what each service takes).
 */
const SendAllServiceRowComponent: React.FC<SendAllServiceRowProps> = ({
  view,
  profile,
  ticks,
  only,
  locked,
  isSending,
  onToggle,
  onRetry,
  onConnect,
}) => {
  const intl = useIntl();
  let account = intl.formatMessage(s.notConnected);
  if (view.comingSoon) {
    account = intl.formatMessage(s.notAvailableYet);
  } else if (view.connected) {
    account =
      view.accountName === null
        ? intl.formatMessage(s.connected, { name: SEND_ALL_SERVICE_NAMES[view.service] })
        : intl.formatMessage(s.connected, { name: view.accountName });
  }
  const testId = `send-all-service-${view.service}`;

  return (
    <section className={clsx(styles.service, view.off && styles.serviceOff)} data-testid={testId}>
      <div className={styles.serviceHead}>
        <span className={styles.logo} data-service={view.service} aria-hidden="true">
          {LOGO[view.service]}
        </span>
        <div className={styles.serviceText}>
          <span className={styles.serviceName}>{SEND_ALL_SERVICE_NAMES[view.service]}</span>
          <span className={styles.itemSub}>{account}</span>
        </div>
        {view.comingSoon && <SendAllChipBadge kind="comingSoon" testId={`${testId}-coming-soon`} />}
        {view.action !== null && (
          <SMLButton
            variant={view.action === 'connect' ? 'primary' : 'default'}
            className={styles.sm}
            onClick={() => onConnect(view.service)}
          >
            {intl.formatMessage(view.action === 'connect' ? s.connect : s.reconnect)}
          </SMLButton>
        )}
      </div>
      <div className={styles.serviceItems}>
        {view.items.map((item) => (
          <SendAllItemRow
            key={item.key}
            view={item}
            profile={profile}
            ticked={isTicked(item, ticks, only)}
            locked={locked}
            isSending={isSending}
            onToggle={onToggle}
            onRetry={onRetry}
            onReconnect={() => onConnect(view.service)}
          />
        ))}
      </div>
      {view.service === 'youtube' && <div className={styles.serviceNote}>{intl.formatMessage(s.noteYoutube)}</div>}
      {view.service === 'facebook' && view.comingSoon && (
        <div className={styles.serviceNote}>{intl.formatMessage(s.noteFacebook)}</div>
      )}
    </section>
  );
};

export const SendAllServiceRow = TypedMemo(SendAllServiceRowComponent);
